/**
 * Error monitoring and reporting utility
 * Integrates with Sentry, Rollbar, or custom error tracking
 */

import { API_CONFIG } from '../utils/config';
import logger from '../utils/logger';
import { hasConsentFor } from '../hooks/useConsent';
import {
  getPrivacySafeCurrentUrl,
  isSensitiveCredentialRoute,
} from './sensitiveRoutePrivacy';

interface ErrorReport {
  message: string;
  context?: Record<string, any>;
  level: 'error' | 'warning' | 'info';
  timestamp: string;
  url?: string;
}

const FRONTEND_ERROR_CATEGORIES = [
  ['ChunkLoadError', /chunk\s*load|loading chunk/i],
  ['ReferenceError', /\breferenceerror\b/i],
  ['SyntaxError', /\bsyntaxerror\b/i],
  ['RangeError', /\brangeerror\b/i],
  ['NetworkError', /network|failed to fetch|fetch failed/i],
  ['TimeoutError', /timeout|timed out|aborterror/i],
  ['AccessError', /unauthorized|forbidden|permission|authentication/i],
  ['ValidationError', /validation|invalid input/i],
  ['TypeError', /\btypeerror\b/i],
] as const;

const classifyError = (error: Error | string): string => {
  const source = error instanceof Error
    ? `${error.name} ${error.message}`
    : String(error);
  return FRONTEND_ERROR_CATEGORIES.find(([, pattern]) => pattern.test(source))?.[0]
    || 'FrontendError';
};

const telemetryTransmissionAllowed = (): boolean => (
  hasConsentFor('analytics') && !isSensitiveCredentialRoute()
);

const sanitizeContext = (context?: Record<string, any>): Record<string, unknown> | undefined => {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return undefined;

  const sanitized: Record<string, unknown> = {};
  if (typeof context.type === 'string' && /^[a-z][a-z0-9_]{0,63}$/i.test(context.type)) {
    sanitized.type = context.type;
  }
  if (context.errorBoundary === true) sanitized.errorBoundary = true;
  for (const key of ['lineno', 'colno'] as const) {
    if (Number.isInteger(context[key]) && context[key] >= 0 && context[key] <= 10_000_000) {
      sanitized[key] = context[key];
    }
  }
  return Object.keys(sanitized).length ? sanitized : undefined;
};

const sanitizeSentryEvent = (event: any) => ({
  ...event,
  message: event.message ? classifyError(String(event.message)) : event.message,
  exception: event.exception?.values
    ? {
      ...event.exception,
      values: event.exception.values.map((value: any) => ({
        ...value,
        type: classifyError(`${value?.type || ''} ${value?.value || ''}`),
        value: 'FrontendError',
      })),
    }
    : event.exception,
  breadcrumbs: undefined,
  contexts: undefined,
  extra: undefined,
  request: event.request
    ? {
      ...event.request,
      url: getPrivacySafeCurrentUrl(),
      query_string: undefined,
      cookies: undefined,
      headers: undefined,
      data: undefined,
    }
    : event.request,
  user: event.user?.id ? { id: String(event.user.id).slice(0, 128) } : undefined,
});

class ErrorMonitoring {
  private initialized = false;
  private debugMode = false;
  private errorQueue: ErrorReport[] = [];
  private maxQueueSize = 50;

  constructor() {
    this.debugMode = process.env.NODE_ENV === 'development';
    this.setupGlobalErrorHandlers();
  }

  /**
   * Initialize error monitoring service (e.g., Sentry)
   */
  init() {
    if (this.initialized || !telemetryTransmissionAllowed()) return;

    try {
      // Initialize Sentry (if available)
      if (window.Sentry) {
        window.Sentry.init({
          dsn: process.env.REACT_APP_SENTRY_DSN || '',
          environment: process.env.NODE_ENV,
          tracesSampleRate: 0.1,
          beforeSend: (event: any, hint: any) => {
            if (!telemetryTransmissionAllowed()) return null;
            // Filter out certain errors
            if (event.exception) {
              const error = hint.originalException;
              if (error instanceof Error) {
                // Don't send network errors in development
                if (this.debugMode && error.message.includes('network')) {
                  return null;
                }
              }
            }
            return sanitizeSentryEvent(event);
          },
          beforeSendTransaction: (event: any) => (
            telemetryTransmissionAllowed() ? event : null
          ),
          beforeBreadcrumb: (breadcrumb: any) => (
            telemetryTransmissionAllowed() ? breadcrumb : null
          ),
        });
      }

      this.initialized = true;
      this.log('Error monitoring initialized');
    } catch (error) {
      logger.error('Failed to initialize error monitoring:', error);
    }
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(event.reason, {
        type: 'unhandled_promise_rejection',
        promise: event.promise
      });
    });

    // Catch global errors
    window.addEventListener('error', (event) => {
      this.captureError(event.error || event.message, {
        type: 'global_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
  }

  /**
   * Capture an error
   */
  captureError(error: Error | string, context?: Record<string, any>) {
    if (isSensitiveCredentialRoute()) return;

    try {
      const safeContext = sanitizeContext(context);
      const errorReport: ErrorReport = {
        message: classifyError(error),
        context: safeContext,
        level: 'error',
        timestamp: new Date().toISOString(),
        url: getPrivacySafeCurrentUrl(),
      };

      // Add to queue
      this.addToQueue(errorReport);

      // Send to Sentry
      if (window.Sentry && telemetryTransmissionAllowed()) {
        if (error instanceof Error) {
          const safeError = new Error(errorReport.message);
          safeError.name = errorReport.message;
          window.Sentry.captureException(safeError, { contexts: { custom: safeContext } });
        } else {
          window.Sentry.captureMessage(classifyError(error), {
            level: 'error',
            contexts: { custom: safeContext }
          });
        }
      }

      // Log in development
      if (this.debugMode) {
        logger.error('[Error Monitoring]', error, context);
      }

      // Send to custom backend endpoint
      if (telemetryTransmissionAllowed()) void this.sendToBackend(errorReport);
    } catch (err) {
      logger.error('Failed to capture error:', err);
    }
  }

  /**
   * Capture a warning
   */
  captureWarning(message: string, context?: Record<string, any>) {
    if (isSensitiveCredentialRoute()) return;

    try {
      const safeContext = sanitizeContext(context);
      const errorReport: ErrorReport = {
        message: classifyError(message),
        context: safeContext,
        level: 'warning',
        timestamp: new Date().toISOString(),
        url: getPrivacySafeCurrentUrl(),
      };

      this.addToQueue(errorReport);

      if (window.Sentry && telemetryTransmissionAllowed()) {
        window.Sentry.captureMessage(errorReport.message, {
          level: 'warning',
          contexts: { custom: safeContext }
        });
      }

      this.log('Warning:', message, context);
    } catch (error) {
      logger.error('Failed to capture warning:', error);
    }
  }

  /**
   * Set user context for error reports
   */
  setUser(userId: string, _email?: string, _metadata?: Record<string, any>) {
    if (!telemetryTransmissionAllowed()) return;

    try {
      if (window.Sentry) {
        window.Sentry.setUser({
          id: userId,
        });
      }

      this.log('User context set:', userId);
    } catch (error) {
      logger.error('Failed to set user context:', error);
    }
  }

  /**
   * Add breadcrumb for debugging context
   */
  addBreadcrumb(message: string, category: string, level: 'info' | 'warning' | 'error' = 'info', data?: Record<string, any>) {
    if (!telemetryTransmissionAllowed()) return;

    try {
      if (window.Sentry) {
        window.Sentry.addBreadcrumb({
          message: 'Application event',
          category: /^[a-z][a-z0-9_.-]{0,63}$/i.test(category) ? category : 'application',
          level,
          data: sanitizeContext(data),
          timestamp: Date.now() / 1000
        });
      }

      this.log('Breadcrumb added:', message, category);
    } catch (error) {
      logger.error('Failed to add breadcrumb:', error);
    }
  }

  /**
   * Add to error queue
   */
  private addToQueue(report: ErrorReport) {
    this.errorQueue.push(report);
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift(); // Remove oldest
    }
  }

  /**
   * Send error to backend
   */
  private async sendToBackend(report: ErrorReport) {
    if (this.debugMode || !telemetryTransmissionAllowed()) return;

    try {
      await fetch(`${API_CONFIG.BACKEND_URL}/api/analytics/error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(report),
        credentials: 'include',
        keepalive: true,
      });
    } catch (error) {
      // Silently fail - don't want to create error loops
      logger.warn('Failed to send error to backend:', error);
    }
  }

  /**
   * Get error queue (for debugging)
   */
  getErrorQueue(): ErrorReport[] {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  clearErrorQueue() {
    this.errorQueue = [];
  }

  private log(...args: any[]) {
    if (this.debugMode) {
      logger.info('[Error Monitoring]', ...args);
    }
  }
}

// Extend Window interface
declare global {
  interface Window {
    Sentry?: any;
  }
}

// Create singleton instance
export const errorMonitoring = new ErrorMonitoring();

export default errorMonitoring;
